import { FilePartReader } from "./file-part-reader.js";

/**
 * @typedef {{parts: any[], count: number, success?: boolean}} MultipartWorkerResult
 */

/**
 * @typedef {{
 *   file: File,
 *   source: ReadableStream<Uint8Array>,
 *   offset: number,
 *   size: number,
 *   partNumberStart: number,
 *   maxPartCount: number,
 *   partSize: number,
 *   retries: number,
 *   getPartSizeMultiple: () => number,
 *   getMinPartSizeMultiple: () => number,
 *   updatePartSize: (success: boolean, multiple: number) => void,
 *   onPartStart: (file: File, index: number) => Promise<{url: string, headers: {[key: string]: string}, method: "POST"|"PUT"}|void>,
 *   onPartComplete: (file: File, xhr: XMLHttpRequest) => Promise<false|any>,
 *   onPartRetry: (file: File, xhr: XMLHttpRequest, loaded: number, total: number, retry: number) => void,
 *   onProgress: (worker: MultipartUploadWorker, loaded: number, retry: number) => void,
 *   onPart: (worker: MultipartUploadWorker, index: number, data: any) => void,
 * }} MultipartUploadWorkerOptions
 */

/**
 * Uploads one bounded stream range sequentially.
 */
export default class MultipartUploadWorker {
  /**
   * @param {MultipartUploadWorkerOptions} options
   */
  constructor(options) {
    this.file = options.file;
    this.offset = options.offset;
    this.size = options.size;
    this.partNumberStart = options.partNumberStart;
    this.maxPartCount = options.maxPartCount;
    this.partSize = options.partSize;
    this.retries = options.retries;
    this.getPartSizeMultiple = options.getPartSizeMultiple;
    this.getMinPartSizeMultiple = options.getMinPartSizeMultiple;
    this.updatePartSize = options.updatePartSize;
    this.onPartStart = options.onPartStart;
    this.onPartComplete = options.onPartComplete;
    this.onPartRetry = options.onPartRetry;
    this.onProgress = options.onProgress;
    this.onPart = options.onPart;
    this.partReader = new FilePartReader(options.source, options.partSize);
    this.parts = [];
    this.loaded = 0;
    this.retry = 0;
    this.abortController = null;
    this.cancelled = false;
    this.settled = false;
  }

  /** @type {File} */
  file;
  /** @type {number} */
  offset;
  /** @type {number} */
  size;
  /** @type {number} */
  partNumberStart;
  /** @type {number} */
  maxPartCount;
  /** @type {number} */
  partSize;
  /** @type {number} */
  retries;
  /** @type {FilePartReader} */
  partReader;
  /** @type {any[]} */
  parts;
  /** @type {number} */
  loaded;
  /** @type {number} */
  retry;
  /** @type {XMLHttpRequest|null} */
  abortController;

  /**
   * @returns {Promise<MultipartWorkerResult>}
   */
  start() {
    return new Promise((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
      this.partReader.onFilePart = (
        /** @type {Blob} */ part,
        /** @type {number} */ index,
      ) =>
        this.#uploadPart(part, index).catch(() => {
          this.#finish(this.resolve, {
            parts: [],
            count: 0,
            success: false,
          });
        });
      this.partReader.onFileEnd = (count) =>
        this.#finish(resolve, { parts: this.parts, count, success: true });
      this.partReader.partSizeMultiple = this.getPartSizeMultiple();
      this.partReader.start();
    });
  }

  abort() {
    this.cancelled = true;
    this.abortController?.abort();
    this.#finish(this.resolve, { parts: [], count: 0, success: false });
  }

  async #uploadPart(
    /** @type {Blob} */ filePart,
    /** @type {number} */ localIndex,
    /** @type {number} */ retries = this.retries,
  ) {
    if (this.settled || this.cancelled) {
      return;
    }
    if (localIndex >= this.maxPartCount) {
      this.#finish(this.resolve, { parts: [], count: 0, success: false });
      return;
    }
    const index = this.partNumberStart - 1 + localIndex;
    const config = await this.onPartStart(this.file, index);
    if (this.settled || this.cancelled) {
      return;
    }
    if (!config) {
      this.#finish(this.resolve, { parts: [], count: 0, success: false });
      return;
    }

    const xhr = new XMLHttpRequest();
    this.abortController = xhr;
    xhr.open(config.method, config.url);
    for (const name in config.headers) {
      xhr.setRequestHeader(name, config.headers[name]);
    }

    const retryCount = this.retry + this.retries - retries;
    this.onProgress(this, this.loaded, retryCount);
    xhr.upload.onprogress = (event) => {
      this.onProgress(this, this.loaded + event.loaded, retryCount);
    };
    xhr.timeout = 30 * 1e3;
    xhr.onloadend = async () => {
      if (this.cancelled) {
        this.#finish(this.resolve, { parts: [], count: 0, success: false });
        return;
      }
      if (xhr.status === 0 || xhr.status >= 500) {
        if (this.getPartSizeMultiple() !== this.getMinPartSizeMultiple()) {
          this.onPartRetry(this.file, xhr, this.loaded, this.size, retryCount);
          this.updatePartSize(false, this.getMinPartSizeMultiple());
          this.partReader.partSizeMultiple = this.getPartSizeMultiple();
          this.retry++;
          await this.partReader.restartFilePart();
          return;
        }
        if (retries !== 0) {
          this.onPartRetry(
            this.file,
            xhr,
            this.loaded,
            this.size,
            retryCount + 1,
          );
          this.onProgress(this, this.loaded, retryCount + 1);
          await new Promise((resolve) =>
            setTimeout(resolve, 100 * 2 ** (this.retries - retries + 1)),
          );
          await this.#uploadPart(filePart, localIndex, retries - 1);
          return;
        }
      }

      const data = await this.onPartComplete(this.file, xhr);
      if (this.settled || this.cancelled) {
        return;
      }
      if (data === false) {
        this.#finish(this.resolve, { parts: [], count: 0, success: false });
        return;
      }
      this.parts[localIndex] = {
        ...data,
        partNumber: index + 1,
      };
      this.loaded += filePart.size;
      if (filePart.size === this.partSize * this.getPartSizeMultiple()) {
        this.updatePartSize(true, this.getPartSizeMultiple());
        this.partReader.partSizeMultiple = this.getPartSizeMultiple();
      }
      this.retry = 0;
      this.abortController = null;
      this.onPart(this, index, data);
      await this.partReader.startNextFilePart();
    };
    xhr.send(filePart);
  }

  /** @type {(result: MultipartWorkerResult) => void} */
  resolve = () => {};
  /** @type {(error: unknown) => void} */
  reject = () => {};
  /** @type {(file: File, index: number) => Promise<any>} */
  onPartStart;
  /** @type {(file: File, xhr: XMLHttpRequest) => Promise<false|any>} */
  onPartComplete;
  /** @type {(file: File, xhr: XMLHttpRequest, loaded: number, total: number, retry: number) => void} */
  onPartRetry;
  /** @type {(worker: MultipartUploadWorker, loaded: number, retry: number) => void} */
  onProgress;
  /** @type {(worker: MultipartUploadWorker, index: number, data: any) => void} */
  onPart;
  /** @type {() => number} */
  getPartSizeMultiple;
  /** @type {() => number} */
  getMinPartSizeMultiple;
  /** @type {(success: boolean, multiple: number) => void} */
  updatePartSize;
  /** @type {boolean} */
  settled;

  /**
   * @param {(result: MultipartWorkerResult) => void} resolve
   * @param {MultipartWorkerResult} result
   */
  #finish(resolve, result) {
    if (this.settled) {
      return;
    }
    this.settled = true;
    resolve(result);
  }
}
